import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function HomePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/`,
      name: 'YESJETS – Unparalleled luxury in private aviation',
      url: siteUrl,
      description: 'Book a private jet charter with YESJETS. Chicago-based luxury aviation with 50+ aircraft, 24/7 concierge, transparent pricing, and world-class safety. Request a quote today.',
      isPartOf: { '@type': 'WebSite', '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      name: 'YESJETS',
      url: siteUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${siteUrl}/destinations?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: 'YESJETS',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/og-image.jpg`,
      },
      description: 'Chicago-based luxury private jet charter services offering exclusive aircraft rentals for discerning travelers worldwide.',
      telephone: '+17085678866',
      email: 'Flights@flyyesjets.com',
      priceRange: '$$$',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Chicago',
        addressRegion: 'IL',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 41.8781,
        longitude: -87.6298,
      },
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
        opens: '00:00',
        closes: '23:59',
      },
      sameAs: [
        'https://www.instagram.com/yesjets',
        'https://www.linkedin.com/company/yesjets',
        'https://www.facebook.com/yesjets',
        'https://twitter.com/yesjets',
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${siteUrl}/#service`,
      name: 'Private Jet Charter',
      serviceType: 'Private Jet Charter',
      provider: { '@type': 'Organization', '@id': `${siteUrl}/#organization` },
      areaServed: { '@type': 'Place', name: 'Worldwide' },
      description: 'On-demand luxury private jet charter with a curated fleet of ultra-long-range, midsize, and light jets. 24/7 concierge, transparent pricing, and world-class safety standards.',
      url: `${siteUrl}/booking`,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="YESJETS – Unparalleled luxury in private aviation"
        description="Book a private jet charter with YESJETS. Chicago-based luxury aviation with 50+ aircraft, 24/7 concierge, transparent pricing, and world-class safety. Request a quote today."
        keywords="private jet charter, Chicago, luxury aircraft rental, YESJETS, private aviation, executive jet charter"
        canonical="/"
        ogType="website"
        jsonLd={jsonLd}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a94f7422b90d96c51ade89d5e365f20a.jpg"
            alt="Luxury Black Private Jet"
            className="w-full h-full object-cover object-left md:object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
        </div>

        <div className="relative h-full flex items-start px-6 md:px-16 max-w-[1920px] mx-auto">
          <div className="w-full pt-24 md:pt-32">
            <h1 className="text-5xl sm:text-6xl md:text-8xl font-serif font-light text-white leading-tight mb-4 md:mb-6">
              ELEVATE YOUR<br />JOURNEY
            </h1>
            <p className="text-base md:text-xl text-gray-300 font-sans mb-8 md:mb-12 max-w-xl">
              Experience unparalleled luxury in private aviation
            </p>
            <div className="flex flex-col md:flex-row gap-3 md:gap-6 items-start">
              <Link
                to="/booking"
                className="inline-flex items-center gap-3 bg-gold text-black px-8 md:px-10 py-3 md:py-4 rounded-full text-sm md:text-base font-sans font-semibold tracking-wide hover:bg-gold/90 transition-all cursor-pointer whitespace-nowrap"
              >
                REQUEST QUOTE
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/time-value"
                className="flex items-center gap-3 bg-white/10 border border-white/40 text-white px-6 md:px-10 py-3 md:py-4 rounded-full text-xs md:text-base font-sans font-semibold tracking-wide hover:bg-white/20 transition-all cursor-pointer backdrop-blur-sm max-w-xs md:max-w-none text-center leading-tight"
              >
                TAKE OUR PRIVATE AVIATION TIME VALUE ASSESSMENT
                <i className="ri-time-line flex-shrink-0"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Fleet Preview Section */}
      <section className="py-16 md:py-24 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-serif font-semibold text-black mb-4">
              OUR EXCLUSIVE FLEET
            </h2>
            <p className="text-base md:text-lg text-gray-600 font-sans">
              Discover our collection of world-class aircraft
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10 mb-10 md:mb-12">
            {[
              {
                name: 'Citation CJ3',
                capacity: '7 Passengers',
                range: '2,000 mile range',
                rate: '$3,200/hr',
                image: 'https://storage.readdy-site.link/project_files/a8799dc5-05d6-47d3-b91a-22752c6df7a4/3a70da06-011b-42e3-8fbe-ab727ff63506_Citation-CJ3.png?v=e980ea487ce596e995d00fd652884aa1',
              },
              {
                name: 'Bombardier Global 5000',
                capacity: '9-14 Passengers',
                range: '5,200 mile range',
                rate: '$9,500/hr',
                image: 'https://storage.readdy-site.link/project_files/a8799dc5-05d6-47d3-b91a-22752c6df7a4/5a16908b-65d8-4556-877c-82b07cf9ab6b_Global-5000.jpg?v=058ff1d4902e3e2e12103ac2b5e6a26b',
              },
              {
                name: 'Citation Sovereign +',
                capacity: '9 Passengers',
                range: '3,000 mile range',
                rate: '$5,500/hr',
                image: 'https://storage.readdy-site.link/project_files/a8799dc5-05d6-47d3-b91a-22752c6df7a4/a3da2349-6a50-4788-804b-a6c3f26dd00c_Citation-Sovereign-.webp?v=f5bd5d88a99015ddec00428358670f37',
              },
            ].map((aircraft, index) => (
              <div
                key={index}
                className="group relative rounded-3xl overflow-hidden cursor-pointer transition-transform hover:scale-[1.03] hover:shadow-2xl"
              >
                <div className="aspect-[4/3] w-full">
                  <img
                    src={aircraft.image}
                    alt={aircraft.name}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: index === 0 ? 'left top' : index === 1 ? 'right top' : index === 2 ? 'left top' : 'center top' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-white">
                  <h3 className="text-xl md:text-2xl font-serif font-semibold mb-2">{aircraft.name}</h3>
                  <p className="text-sm font-sans text-gray-300 mb-3">{aircraft.capacity} • {aircraft.range}</p>
                  <div className="flex items-center gap-2 text-sm font-sans">
                    <span>VIEW DETAILS</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/planes" className="inline-flex items-center gap-2 text-black font-sans font-semibold hover:text-gold transition-colors cursor-pointer whitespace-nowrap">
              VIEW FULL FLEET
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Why YESJETS Section */}
      <section className="py-16 md:py-24 bg-navy">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-serif font-semibold text-white mb-2">
              THE YESJETS DIFFERENCE
            </h2>
            <div className="w-24 h-1 bg-gold mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className="space-y-10 md:space-y-16">
              {[
                { icon: 'ri-shield-check-line', title: 'Safety First', description: 'Rigorous maintenance standards and experienced crew ensure your complete peace of mind' },
                { icon: 'ri-time-line', title: '24/7 Availability', description: 'Book your flight anytime with our round-the-clock concierge service' },
                { icon: 'ri-vip-crown-line', title: 'Luxury Experience', description: 'Personalized service and premium amenities tailored to your preferences' },
                { icon: 'ri-global-line', title: 'Global Network', description: 'Access to premier destinations worldwide with seamless connections' },
              ].map((benefit, index) => (
                <div key={index} className="flex gap-5 md:gap-6">
                  <div className="w-14 h-14 rounded-full border-2 border-gold flex items-center justify-center flex-shrink-0">
                    <i className={`${benefit.icon} text-xl md:text-2xl text-white`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-sans font-semibold text-white mb-2">{benefit.title}</h3>
                    <p className="text-gray-400 font-sans leading-relaxed text-sm md:text-base">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative hidden lg:block">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20luxury%20private%20jet%20interior%20detail%20composition%20featuring%20premium%20cream%20leather%20executive%20seats%20with%20gold%20accent%20stitching%20crystal%20champagne%20flutes%20on%20polished%20wood%20table%20and%20sophisticated%20ambient%20lighting%20against%20soft%20neutral%20beige%20background%20creating%20an%20atmosphere%20of%20refined%20opulence%20and%20exclusive%20business%20aviation%20comfort&width=800&height=1000&seq=interior-luxury-001&orientation=portrait"
                alt="Luxury Interior"
                className="w-full h-[600px] object-cover rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Destinations Preview */}
      <section className="py-16 md:py-24 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-serif font-semibold text-black mb-4">
              PREMIER DESTINATIONS
            </h2>
            <p className="text-base md:text-lg text-gray-600 font-sans">
              Fly to the world's most exclusive locations
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {[
              { name: 'Monaco', routes: '24', image: 'https://readdy.ai/api/search-image?query=Breathtaking%20aerial%20view%20of%20Monaco%20harbor%20featuring%20luxury%20yachts%20moored%20in%20the%20pristine%20azure%20Mediterranean%20waters%20surrounded%20by%20elegant%20Belle%20%C3%89poque%20architecture%20and%20lush%20green%20hillsides%20with%20soft%20golden%20hour%20lighting%20against%20a%20clean%20gradient%20background%20from%20soft%20cream%20to%20pale%20champagne%20emphasizing%20the%20glamorous%20French%20Riviera%20destination&width=800&height=600&seq=dest-monaco-001&orientation=landscape' },
              { name: 'Los Angeles', routes: '28', image: 'https://readdy.ai/api/search-image?query=Stunning%20Los%20Angeles%20cityscape%20panorama%20featuring%20the%20iconic%20Hollywood%20Hills%20and%20downtown%20skyscrapers%20glowing%20under%20a%20warm%20golden%20sunset%20with%20palm%20trees%20lining%20wide%20boulevards%20and%20the%20Pacific%20Ocean%20shimmering%20in%20the%20distance%20against%20a%20soft%20gradient%20background%20from%20warm%20amber%20to%20pale%20champagne%20evoking%20the%20glamour%20and%20luxury%20of%20Southern%20California&width=800&height=600&seq=dest-la-001&orientation=landscape' },
              { name: 'Aspen', routes: '18', image: 'https://readdy.ai/api/search-image?query=Majestic%20snow-covered%20Aspen%20mountain%20peaks%20with%20pristine%20white%20powder%20slopes%20and%20evergreen%20pine%20forests%20under%20clear%20blue%20Colorado%20sky%20with%20soft%20gradient%20background%20from%20pale%20ivory%20to%20light%20champagne%20highlighting%20the%20exclusive%20winter%20resort%20destination%20and%20alpine%20luxury%20skiing%20experience&width=800&height=600&seq=dest-aspen-001&orientation=landscape' },
            ].map((dest, index) => (
              <div key={index} className="group relative rounded-2xl overflow-hidden cursor-pointer h-56 md:h-64">
                <img src={dest.image} alt={dest.name} className="w-full h-full object-cover object-top transition-transform group-hover:scale-110" />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-all"></div>
                <div className="absolute bottom-5 left-5 text-white">
                  <h3 className="text-2xl md:text-3xl font-serif font-semibold mb-1">{dest.name}</h3>
                  <p className="text-sm text-gold font-sans">{dest.routes} Popular Routes</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/destinations" className="inline-flex items-center gap-2 text-black font-sans font-semibold hover:text-gold transition-colors cursor-pointer whitespace-nowrap">
              VIEW ALL DESTINATIONS
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-charcoal">
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-serif font-light text-white mb-5 md:mb-6">
            Ready to Elevate Your Travel?
          </h2>
          <p className="text-base md:text-xl text-gray-400 font-sans mb-8 md:mb-10">
            Book your private jet experience today and discover the ultimate in luxury aviation
          </p>
          <Link
            to="/booking"
            className="inline-flex items-center gap-3 bg-gold text-black px-8 md:px-10 py-3 md:py-4 rounded-full text-sm md:text-base font-sans font-semibold tracking-wide hover:bg-gold/90 transition-all cursor-pointer whitespace-nowrap"
          >
            START BOOKING
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
